import { mapEventBooking, MeetingCard } from '@/features/interview-scheduling/meeting-card'
import type { CandidateModel } from '@/ui/views/shared/candidate'
import { useCandidateEventIndex } from '@jobilla/nexus-sdk'
import { isToday } from 'date-fns'
import { useEffect, useState } from 'react'

/**
 * A `now` (epoch ms) that re-renders every 30s while `active`, so the panel's countdown
 * and join state stay current as the interview approaches, starts, and ends — no reload.
 */
function useNow(active: boolean): number {
	const [now, setNow] = useState(() => Date.now())

	useEffect(() => {
		if (!active) {
			return
		}
		const timer = setInterval(() => setNow(Date.now()), 30_000)

		return () => clearInterval(timer)
	}, [active])

	return now
}

/**
 * Surfaces the candidate's next confirmed interview on the overview — the soonest one whose
 * end is still ahead, however far in the future — reusing the timeline's meeting card (live
 * countdown + join button). Gated on the lean board booking so it only fetches/ticks for
 * candidates who actually have an upcoming interview.
 */
export function NextInterviewPanel({ candidateId, interviewBooking, onJoinInApp }: {
	candidateId: string
	interviewBooking: CandidateModel['interviewBooking']
	onJoinInApp?: () => void
}) {
	// Gate the fetch on the lean board booking: a confirmed interview that is upcoming or
	// still today (which also covers one already in progress, started earlier today).
	const hasUpcoming = interviewBooking?.status === 'confirmed'
		&& (interviewBooking.scheduledAt.getTime() > Date.now() || isToday(interviewBooking.scheduledAt))
	const { data: eventIndex } = useCandidateEventIndex(candidateId, { enabled: hasUpcoming })
	const now = useNow(hasUpcoming)

	if (!hasUpcoming) {
		return null
	}

	// The next interview: the soonest confirmed booking whose end is still ahead (upcoming
	// or in progress). Anything already finished drops out.
	const nextBooking = (eventIndex?.events ?? [])
		.map((event) => event.booking)
		.filter((booking): booking is NonNullable<typeof booking> =>
			booking != null
			&& booking.status === 'confirmed'
			&& new Date(booking.meeting.endsAt).getTime() > now
		)
		.sort((first, second) =>
			new Date(first.meeting.startsAt).getTime() - new Date(second.meeting.startsAt).getTime()
		)[0]

	if (nextBooking == null) {
		return null
	}

	return <MeetingCard meeting={mapEventBooking(nextBooking).meeting} onJoinInApp={onJoinInApp} />
}
